import { XMarkIcon } from "@heroicons/react/24/outline";
import { KeywordBuckets } from "./types";

type KeywordSectionProps = {
  positive: KeywordBuckets;
  negative: KeywordBuckets;
  onAddCategory: (group: "positive" | "negative") => void;
  onRemoveCategory: (group: "positive" | "negative", category: string) => void;
  onRenameCategory: (group: "positive" | "negative", category: string, nextName: string) => void;
  onAddKeyword: (group: "positive" | "negative", category: string) => void;
  onRemoveKeyword: (group: "positive" | "negative", category: string, index: number) => void;
  onKeywordChange: (group: "positive" | "negative", category: string, index: number, value: string) => void;
};

function KeywordGroup({
  title,
  value,
  group,
  onAddCategory,
  onRemoveCategory,
  onRenameCategory,
  onAddKeyword,
  onRemoveKeyword,
  onKeywordChange,
}: {
  title: string;
  value: KeywordBuckets;
  group: "positive" | "negative";
} & Omit<KeywordSectionProps, "positive" | "negative">) {
  const categories = Object.entries(value);
  const isPositive = group === "positive";

  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h4 className={`text-sm font-semibold ${isPositive ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}>
          {title}
        </h4>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg border border-dashed border-entntblue/40 px-2.5 py-1.5 text-xs font-medium text-entntblue hover:border-entntblue hover:bg-entntblue/5 transition-colors"
          onClick={() => onAddCategory(group)}
        >
          <span className="text-sm leading-none">+</span> Category
        </button>
      </div>

      <div className="px-4 py-3 space-y-3">
        {categories.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-gray-500 py-2 text-center">No categories yet</p>
        )}
        {categories.map(([category, keywords], categoryIndex) => (
          <div key={`${group}-${categoryIndex}`} className="rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center gap-2 bg-white dark:bg-darkbg px-3 py-2 border-b border-gray-100 dark:border-gray-700">
              <input
                value={category}
                className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 py-1.5 text-sm font-medium text-gray-800 dark:text-gray-100 bg-white dark:bg-darkbg focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 transition-colors"
                onChange={(e) => onRenameCategory(group, category, e.target.value)}
                placeholder="Category name"
              />
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-300 dark:border-red-700 text-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                onClick={() => onRemoveCategory(group, category)}
                aria-label="Remove category"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="px-3 py-2.5 space-y-1.5">
              {keywords.map((keyword, idx) => (
                <div key={`${group}-${category}-${idx}`} className="grid grid-cols-[1fr_32px] items-center gap-2">
                  <input
                    value={keyword}
                    className="rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-100 bg-white dark:bg-darkbg placeholder-gray-400 focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 transition-colors"
                    onChange={(e) => onKeywordChange(group, category, idx, e.target.value)}
                    placeholder="Enter keyword…"
                  />
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-300 dark:border-red-700 text-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => onRemoveKeyword(group, category, idx)}
                    aria-label="Remove keyword"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-entntblue hover:underline transition-colors"
                onClick={() => onAddKeyword(group, category)}
              >
                <span className="text-sm leading-none">+</span> Add keyword
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function KeywordSection(props: KeywordSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <KeywordGroup title="Positive Keywords" value={props.positive} group="positive" {...props} />
      <KeywordGroup title="Negative Keywords" value={props.negative} group="negative" {...props} />
    </div>
  );
}
